import { useState, useEffect } from 'react';
import { IconClose, IconEyeClosed, IconEye, TextField, TextFieldButton } from 'src/viewLayer/components/DesignSystem';

import { t } from 'src/viewLayer/helpers/translations';
import { inputConfig } from './config';
import { InputProps, TextActionButtonProps, PasswordActionButtonProps } from './types';
import { getInputIcon } from './getInputIcon';

const ShowHidePasswordButton = ({ showPassword, setShowPassword }: PasswordActionButtonProps): JSX.Element => (
  <TextFieldButton
    onClick={ (): void => setShowPassword((previous) => !previous) }
    icon={ showPassword ? <IconEyeClosed /> : <IconEye /> }
    title={ showPassword ? 'Show password' : 'Hide password' }
  />
);

const ClearTextButton = ({ value, clearInput }: TextActionButtonProps): JSX.Element => {
  if (!value) return undefined;
  return (
    <TextFieldButton
      onClick={ (): void => clearInput() }
      icon={ <IconClose /> }
      title="Clear"
    />
  );
};

export const Input = ({ currentInput, inputName }: InputProps): JSX.Element => {
  const iconName = inputConfig[inputName]?.icon || undefined;
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const inputType = inputConfig[inputName].type;
  const inputTypeToShow = (inputType === 'password' && showPassword)
    ? 'text'
    : inputConfig[inputName].type;

  // The following fixes POP-3394 caused by the autoFocus prop in the input
  useEffect(() => {
    const inputElement: HTMLInputElement = document.querySelector('.input-field');
    if (inputElement && currentInput.isFocused) {
      inputElement.select();
    }
  }, [currentInput.isFocused]);
  const clearInput = (): void => {
    currentInput.setInputValue('');
    currentInput.resetErrorMessage();
  };

  return (
    <TextField
      aria-describedby={`message-uat-input-${inputName}`}
      color="lightened"
      additionalText={ currentInput.errorMessage && t(currentInput.errorMessage) }
      data-testid="input-field"
      fullWidth
      icon={ iconName && getInputIcon(iconName) }
      actionButton={
        inputType === 'password'
          ? <ShowHidePasswordButton showPassword={ showPassword } setShowPassword={ setShowPassword } />
          : <ClearTextButton value={ currentInput.inputValue } clearInput={ clearInput } />
      }
      id={ `uat-input-${inputName}` }
      className="input-field"
      onInput={(event): void => currentInput.setInputValue((event.target as HTMLInputElement).value)}
      placeholder={ t(inputConfig[inputName].placeholder) }
      state={ currentInput.errorMessage ? 'error' : 'default' }
      type={ inputTypeToShow as 'text' | 'password' | 'email' }
      value={ currentInput.inputValue }
    />
  );
};
